import type { EntityViewLevel, LayerModel, LogicalFinca, Selection } from '../core/types';
import { normalizedFieldName } from '../domain/attributes';
import { icon } from './icons';

const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const normalize = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
const ZONE_FIELDS = new Set(['zona', 'zon', 'zone']);
const NO_ZONE = '__sin_zona__';

/** Zone of a finca: the unique, non-empty source "Zona" value of its fragments (null when absent or ambiguous). */
export function fincaZone(model: LayerModel, finca: LogicalFinca): string | null {
  const values = new Set<string>();
  for (const id of finca.fragmentIds) {
    const attributes = model.fragmentsById.get(id)?.attributes; if (!attributes) continue;
    const key = Object.keys(attributes).find((name) => ZONE_FIELDS.has(normalizedFieldName(name)));
    const value = key ? attributes[key] : null;
    if (value !== null && value !== undefined && String(value).trim()) values.add(String(value).trim());
    if (values.size > 1) return null;
  }
  return values.values().next().value ?? null;
}

const zoneCache = new WeakMap<LayerModel, ReadonlyMap<string, string>>();
const zonesFor = (model: LayerModel): ReadonlyMap<string, string> => {
  const cached = zoneCache.get(model); if (cached) return cached;
  const zones = new Map([...model.fincas.values()].map((finca) => [finca.id, fincaZone(model, finca) ?? NO_ZONE]));
  zoneCache.set(model, zones);
  return zones;
};

/**
 * Explorer: lazy Zona → Finca → Suerte → Tablón tree. Only expanded nodes are rendered, so the
 * Maestro never materialises thousands of rows. It reflects scope + filters through the visible set.
 */
export class Explorer {
  private query = '';
  private readonly expanded = new Set<string>();
  private lastKey = '';
  private lastVisible: ReadonlySet<string> | undefined;
  private lastModel: LayerModel | null = null;
  private context: { model: LayerModel | null; selection: Selection; visible?: ReadonlySet<string> } = { model: null, selection: null };

  constructor(private readonly element: HTMLElement, private readonly select: (kind: EntityViewLevel, id: string) => void, search?: HTMLInputElement | null) {
    element.addEventListener('click', (event) => this.onClick(event));
    search?.addEventListener('input', () => { this.query = normalize(search.value.trim()); this.lastKey = ''; this.draw(); });
  }

  render(model: LayerModel | null, selection: Selection, visibleFragmentIds?: ReadonlySet<string>): void {
    this.context = { model, selection, visible: visibleFragmentIds };
    if (selection && model) this.revealSelection(model, selection);
    this.draw();
  }

  private revealSelection(model: LayerModel, selection: NonNullable<Selection>): void {
    const fragment = selection.kind === 'finca' ? null : model.fragmentsById.get(selection.kind === 'suerte' ? model.suertes.get(selection.id)?.fragmentIds[0] ?? '' : selection.kind === 'tablon' ? model.tablones.get(selection.id)?.fragmentIds[0] ?? '' : selection.id);
    const fincaId = selection.kind === 'finca' ? selection.id : fragment?.fincaId;
    if (fincaId) this.expanded.add(`zone:${zonesFor(model).get(fincaId) ?? NO_ZONE}`);
    if (fragment) { this.expanded.add(fragment.fincaId); if (selection.kind === 'tablon' || selection.kind === 'fragment') this.expanded.add(fragment.suerteId); }
  }

  private draw(): void {
    const { model, selection, visible } = this.context;
    // The selection is not part of the structural key: a selection-only change just moves the highlight.
    const key = `${model?.id ?? ''}|${this.query}|${[...this.expanded].join(',')}`;
    if (key === this.lastKey && visible === this.lastVisible && model === this.lastModel) { this.markSelection(selection); return; }
    this.lastKey = key; this.lastVisible = visible; this.lastModel = model;
    if (!model) { this.element.innerHTML = '<div class="empty-state">Carga el Maestro para explorar<br />Zona → Finca → Suerte → Tablón.</div>'; return; }
    const isVisible = (ids: readonly string[]): boolean => !visible || ids.some((id) => visible.has(id));
    const zones = zonesFor(model);
    const fincas = [...model.fincas.values()].filter((finca) => isVisible(finca.fragmentIds)).filter((finca) => !this.query || this.matchesFinca(model, finca)).sort((a, b) => collator.compare(a.name, b.name));
    if (!fincas.length) { this.element.innerHTML = `<div class="empty-state">${this.query ? 'Sin coincidencias.' : 'No hay entidades visibles con el ámbito y filtros actuales.'}</div>`; return; }
    const groups = new Map<string, LogicalFinca[]>();
    fincas.forEach((finca) => { const zone = zones.get(finca.id) ?? NO_ZONE; groups.set(zone, [...(groups.get(zone) ?? []), finca]); });
    const grouped = groups.size > 1 || !groups.has(NO_ZONE);
    const html: string[] = [];
    [...groups.entries()].sort(([a], [b]) => a === NO_ZONE ? 1 : b === NO_ZONE ? -1 : collator.compare(a, b)).forEach(([zone, items]) => {
      const zoneKey = `zone:${zone}`;
      const zoneOpen = !grouped || this.query !== '' || this.expanded.has(zoneKey) || groups.size === 1;
      if (grouped) html.push(this.node({ depth: 0, key: zoneKey, label: zone === NO_ZONE ? 'Sin zona' : `Zona ${zone}`, meta: `${items.length}`, open: zoneOpen, toggleOnly: true }));
      if (!zoneOpen) return;
      items.forEach((finca) => {
        const fincaOpen = this.expanded.has(finca.id) || (this.query !== '' && this.suerteMatches(model, finca));
        const suertes = finca.suerteIds.map((id) => model.suertes.get(id)!).filter((suerte) => suerte && isVisible(suerte.fragmentIds));
        html.push(this.node({ depth: grouped ? 1 : 0, key: finca.id, kind: 'finca', id: finca.id, label: finca.name, code: finca.code, meta: `${suertes.length} S`, open: fincaOpen, selected: selection?.kind === 'finca' && selection.id === finca.id }));
        if (!fincaOpen) return;
        suertes.sort((a, b) => collator.compare(a.label, b.label)).forEach((suerte) => {
          const suerteOpen = this.expanded.has(suerte.id);
          const tablones = suerte.tablonIds.map((id) => model.tablones.get(id)!).filter((tablon) => tablon && isVisible(tablon.fragmentIds));
          html.push(this.node({ depth: grouped ? 2 : 1, key: suerte.id, kind: 'suerte', id: suerte.id, label: `Suerte ${suerte.label}`, meta: `${tablones.length} T`, open: suerteOpen, selected: selection?.kind === 'suerte' && selection.id === suerte.id }));
          if (!suerteOpen) return;
          tablones.sort((a, b) => collator.compare(a.code, b.code)).forEach((tablon) => html.push(this.node({ depth: grouped ? 3 : 2, key: tablon.id, kind: 'tablon', id: tablon.id, label: tablon.code === '__sin_tablon__' ? 'Sin tablón' : `Tablón ${tablon.code}`, meta: `${tablon.fragmentIds.length} fr`, leaf: true, selected: selection?.kind === 'tablon' && selection.id === tablon.id })));
        });
      });
    });
    this.element.innerHTML = html.join('');
  }

  private markSelection(selection: Selection): void {
    this.element.querySelectorAll<HTMLElement>('.tree-node.selected').forEach((node) => node.classList.remove('selected'));
    if (!selection) return;
    this.element.querySelectorAll<HTMLElement>(`.tree-node[data-tree-kind="${selection.kind}"]`).forEach((node) => {
      if (node.dataset.treeId === selection.id) node.classList.add('selected');
    });
  }

  private matchesFinca(model: LayerModel, finca: LogicalFinca): boolean {
    return normalize(`${finca.name} ${finca.code}`).includes(this.query) || this.suerteMatches(model, finca);
  }

  private suerteMatches(model: LayerModel, finca: LogicalFinca): boolean {
    return finca.suerteIds.some((id) => { const suerte = model.suertes.get(id); return Boolean(suerte && normalize(`suerte ${suerte.label} ${suerte.code}`).includes(this.query)); });
  }

  private node(options: { depth: number; key: string; label: string; meta?: string; code?: string; kind?: EntityViewLevel; id?: string; open?: boolean; leaf?: boolean; selected?: boolean; toggleOnly?: boolean }): string {
    const caret = `<span class="caret ${options.leaf ? 'leaf' : options.open ? 'open' : ''}" data-tree-toggle="${esc(options.key)}">${icon('chevronRight')}</span>`;
    const attributes = options.toggleOnly ? `data-tree-toggle="${esc(options.key)}"` : `data-tree-kind="${options.kind}" data-tree-id="${esc(options.id!)}"`;
    return `<button type="button" role="treeitem" class="tree-node${options.selected ? ' selected' : ''}${options.toggleOnly ? ' tree-zone' : ''}" style="--depth:${options.depth}" ${attributes} aria-expanded="${options.leaf ? 'false' : String(Boolean(options.open))}">${caret}<span class="tree-node-text"><span class="tree-node-label">${esc(options.label)}</span>${options.code ? `<span class="tree-node-code">${esc(options.code)}</span>` : ''}</span><span class="tree-node-meta">${esc(options.meta ?? '')}</span></button>`;
  }

  private onClick(event: Event): void {
    const target = event.target as Element;
    const toggle = target.closest<HTMLElement>('[data-tree-toggle]');
    const node = target.closest<HTMLElement>('.tree-node');
    if (toggle && (toggle.classList.contains('caret') || node?.classList.contains('tree-zone'))) {
      const key = toggle.dataset.treeToggle!;
      if (this.expanded.has(key)) this.expanded.delete(key); else this.expanded.add(key);
      this.lastKey = ''; this.draw();
      return;
    }
    if (node?.dataset.treeKind && node.dataset.treeId) {
      if (node.dataset.treeKind !== 'tablon') this.expanded.add(node.dataset.treeId);
      this.select(node.dataset.treeKind as EntityViewLevel, node.dataset.treeId);
    }
  }
}
